/**
 * B-015 — the scoreboard: each agent vs SPY, QQQ, JEPI, QYLD and a YieldMax fund.
 *
 * One component, two placements: the Performance tab of AgentDetailModal (`agentId` set: that agent
 * plus the five benchmarks) and AgentsPage (no `agentId`: all six agents plus the five benchmarks).
 * The data comes from GET /scoreboard, which reads the STORED series (never MarketData) and computes
 * every row in scoreboard_service.compute_scoreboard.
 *
 * Rules: every agent and every benchmark is its own row (never a desk aggregate or a range); a number
 * that cannot be computed shows "—", never 0; a fund not stored yet says "not available yet"; a
 * fetch failure shows an explicit error (never an empty table); dates go through utils/format.
 *
 * B-043: JEPI, QYLD and YMAX are scored exactly like SPY and QQQ, from the vendor's dividend-adjusted closes
 * (every distribution reinvested; "*" while the as-of close is pending), with "price only" (distributions left
 * out) under them.
 *
 * B-050 (owner, Oct 6, 2026: "we don't need this table to be open, it can be minimized below the charts and
 * open if i need to see details"): in both placements the table sits below the chart, collapsed under one
 * "Scoreboard details" line until clicked. Opened, its figures and "as of" line are unchanged. It loads on first
 * open (it reads every agent's NAV history), and the footnotes no longer call "price only" the chart: the chart
 * above it now draws the funds' total return.
 */
import React, { useCallback, useEffect, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { Scoreboard as ScoreboardData, ScoreboardAgentRow, ScoreboardBenchmarkRow } from '../../types';
import { formatDateWithYear, formatSignedPct } from '../../utils/format';
import { pnlColor } from '../../utils/pnl';

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: ScoreboardData };

const Pct: React.FC<{ value: number | null | undefined }> = ({ value }) =>
  value === null || value === undefined ? (
    <span className="text-slate-500">—</span>
  ) : (
    <span className={pnlColor(value)}>{formatSignedPct(value)}</span>
  );

// Max drawdown is <= 0 by construction: 0 is a real "never fell", shown neutral, not as a green "+0.00%".
const Drawdown: React.FC<{ value: number | null | undefined }> = ({ value }) =>
  value === null || value === undefined ? (
    <span className="text-slate-500">—</span>
  ) : (
    <span className={value < 0 ? 'text-down' : 'text-slate-400'}>{`${value.toFixed(2)}%`}</span>
  );

const COLUMNS = ['Since inception', '13 weeks', 'YTD', 'Max drawdown'] as const;

const StatusCell: React.FC<{ text: string; tone?: 'muted' | 'warn' | 'error' }> = ({ text, tone = 'muted' }) => (
  <td colSpan={COLUMNS.length} className={`px-2 py-1.5 text-right ${
    tone === 'error' ? 'text-red-400' : tone === 'warn' ? 'text-amber-400' : 'text-slate-500'
  }`}>
    {text}
  </td>
);

const AgentRow: React.FC<{ row: ScoreboardAgentRow }> = ({ row }) => (
  <tr className="border-t border-slate-700/30" data-testid={`scoreboard-row-${row.id}`}>
    <th scope="row" className="px-2 py-1.5 text-left font-medium text-white whitespace-nowrap">
      {row.name}
      {row.skipped_docs && row.skipped_docs.length > 0 && (
        <span className="ml-1 text-9 text-amber-400" data-testid={`scoreboard-skipped-${row.id}`}
          title="Unreadable NAV records were skipped: the figures exclude those days">
          ({row.skipped_docs.length} NAV record{row.skipped_docs.length === 1 ? '' : 's'} skipped)
        </span>
      )}
    </th>
    {row.status === 'ok' ? (
      <>
        <td className="px-2 py-1.5 text-right tabular-nums"><Pct value={row.since_inception} /></td>
        <td className="px-2 py-1.5 text-right tabular-nums"><Pct value={row.weeks_13} /></td>
        <td className="px-2 py-1.5 text-right tabular-nums" data-testid={`scoreboard-ytd-${row.id}`}>
          <Pct value={row.ytd} />
          {row.ytd_partial && row.ytd !== null && row.ytd !== undefined && (
            <span className="ml-0.5 text-amber-400" title="From the agent's first NAV of the year, not the year's first close">†</span>
          )}
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums"><Drawdown value={row.max_drawdown} /></td>
      </>
    ) : (
      <StatusCell text={`unavailable: ${row.error ?? 'no NAV history'}`} tone="error" />
    )}
  </tr>
);

type Triple = { since_inception: number | null; weeks_13: number | null; ytd: number | null };

const SubRow: React.FC<{ testId: string; label: string; values: Triple; why?: string | null }> = (
  { testId, label, values, why }) => (
  <tr data-testid={testId}>
    <th scope="row" className="px-2 pb-1.5 text-left text-10 font-normal text-slate-500" title={why ?? undefined}>
      {label}
    </th>
    <td className="px-2 pb-1.5 text-right text-10 tabular-nums"><Pct value={values.since_inception} /></td>
    <td className="px-2 pb-1.5 text-right text-10 tabular-nums"><Pct value={values.weeks_13} /></td>
    <td className="px-2 pb-1.5 text-right text-10 tabular-nums"><Pct value={values.ytd} /></td>
    <td className="px-2 pb-1.5" />
  </tr>
);

const BenchmarkRow: React.FC<{ row: ScoreboardBenchmarkRow }> = ({ row }) => {
  const label = (
    <th scope="row" className="px-2 py-1.5 text-left font-medium text-slate-200 whitespace-nowrap">
      {row.symbol}
      {row.as_of_provisional && (
        <span className="ml-1 text-9 text-amber-400" title="The as-of close is still provisional">*</span>
      )}
    </th>
  );
  if (row.status === 'not_available') {
    return (
      <tr className="border-t border-slate-700/30" data-testid={`scoreboard-row-${row.symbol}`}>
        {label}
        <StatusCell text="not available yet" />
      </tr>
    );
  }
  if (row.status === 'stale') {
    return (
      <tr className="border-t border-slate-700/30" data-testid={`scoreboard-row-${row.symbol}`}>
        {label}
        <StatusCell tone="warn" text={row.last_date
          ? `stale: last close ${formatDateWithYear(row.last_date)}`
          : `stale: ${row.reason ?? 'no close on the as-of date'}`} />
      </tr>
    );
  }
  if (row.status === 'failed') {
    return (
      <tr className="border-t border-slate-700/30" data-testid={`scoreboard-row-${row.symbol}`}>
        {label}
        <StatusCell text={`unavailable: ${row.reason ?? 'series could not be read'}`} tone="error" />
      </tr>
    );
  }
  if (row.status !== 'ok') {
    // A status this page does not know (e.g. an older backend's 'withheld'): say so, never render numbers.
    return (
      <tr className="border-t border-slate-700/30" data-testid={`scoreboard-row-${row.symbol}`}>
        {label}
        <StatusCell text={`${row.status}: ${row.reason ?? 'no figures'}`} tone="warn" />
      </tr>
    );
  }
  return (
    <>
      <tr className="border-t border-slate-700/30" data-testid={`scoreboard-row-${row.symbol}`}>
        {label}
        <td className="px-2 py-1.5 text-right tabular-nums"><Pct value={row.since_inception} /></td>
        <td className="px-2 py-1.5 text-right tabular-nums"><Pct value={row.weeks_13} /></td>
        <td className="px-2 py-1.5 text-right tabular-nums"><Pct value={row.ytd} /></td>
        <td className="px-2 py-1.5 text-right tabular-nums"><Drawdown value={row.max_drawdown} /></td>
      </tr>
      {row.price && (
        <SubRow testId={`scoreboard-price-${row.symbol}`} label="price only" values={row.price} why={row.price_reason} />
      )}
    </>
  );
};

/** "Jan 2, 2026" when every item shares one date; otherwise "Jan 2, 2026 (SPY, QQQ), Jan 5, 2026 (YMAX)". */
export const byDate = (items: { label: string; date: string }[]): string => {
  const groups = new Map<string, string[]>();
  items.forEach(({ label, date }) => groups.set(date, [...(groups.get(date) ?? []), label]));
  const dates = Array.from(groups.keys()).sort();
  return dates.length === 1
    ? formatDateWithYear(dates[0])
    : dates.map((d) => `${formatDateWithYear(d)} (${(groups.get(d) ?? []).join(', ')})`).join(', ');
};

export const ScoreboardTable: React.FC<{ data: ScoreboardData; agentId?: string; onRetry?: () => void }> = (
  { data, agentId, onRetry }) => {
  if (data.status !== 'ok') {
    // The service answers 'unavailable' only when no agent's NAV history can be read: a failure, not
    // the pre-backfill state (that is a fund row saying "not available yet").
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-xs"
        role="alert" data-testid="scoreboard-unavailable">
        Scoreboard unavailable: {data.error ?? 'no data'}
        {onRetry && <button onClick={onRetry} className="ml-3 underline">Retry</button>}
      </div>
    );
  }
  const agents = agentId ? data.agents.filter((a) => a.id === agentId) : data.agents;
  const hasProvisional = data.benchmarks.some((b) => b.as_of_provisional);
  const shown = data.benchmarks.filter((b) => b.status === 'ok');
  const fundYtd = shown.map((b) => ({ label: b.symbol, date: b.ytd_from }))
    .filter((x): x is { label: string; date: string } => !!x.date);
  const incomeShown = shown.some((b) => b.kind === 'income');
  const partialYtd = agents.filter((a) => a.status === 'ok' && a.ytd_partial && a.ytd_from
    && a.ytd !== null && a.ytd !== undefined).map((a) => ({ label: a.name, date: a.ytd_from as string }));
  const cells = (r: { since_inception?: number | null; weeks_13?: number | null; ytd?: number | null;
    max_drawdown?: number | null; price?: ScoreboardBenchmarkRow['price'] }) =>
    [r.since_inception, r.weeks_13, r.ytd, r.max_drawdown,
      ...(r.price ? [r.price.since_inception, r.price.weeks_13, r.price.ytd] : [])];
  const hasDash = [...agents.filter((a) => a.status === 'ok'), ...shown].map(cells)
    .some((c) => c.some((v) => v === null || v === undefined));
  return (
    <>
      <p className="text-10 text-slate-500 mb-2" data-testid="scoreboard-dates">
        Total return as of {formatDateWithYear(data.as_of ?? undefined)} · since{' '}
        {formatDateWithYear(data.inception ?? undefined)} · 13 weeks from{' '}
        {formatDateWithYear(data.base_13w ?? undefined)}
      </p>
      <div className="overflow-x-auto">
        {/* Not stretched, with four even figure columns from md up (owner, Oct 2: at full width the long drawdown
            header took the slack and left a wide gap after YTD); the drawdown date sits on its own line */}
        <table className="w-auto text-xs">
          <thead>
            <tr className="text-10 text-slate-500 uppercase tracking-wider">
              <th scope="col" className="px-2 py-1 text-left font-medium" />
              {COLUMNS.map((c) => (
                <th key={c} scope="col" className="px-2 py-1 text-right font-medium whitespace-nowrap align-bottom md:w-32">
                  {/* The Performance tab's drawdown card below runs to today: this one stops at the as-of. */}
                  {c === 'Max drawdown'
                    ? <>{'Max drawdown '}<br />{`(to ${formatDateWithYear(data.as_of ?? undefined)})`}</>
                    : c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agentId && agents.length === 0 && (
              <tr className="border-t border-slate-700/30" data-testid="scoreboard-agent-missing">
                <td colSpan={COLUMNS.length + 1} className="px-2 py-1.5 text-amber-400">
                  This agent is not on the scoreboard.
                </td>
              </tr>
            )}
            {agents.map((a) => <AgentRow key={a.id} row={a} />)}
            {data.benchmarks.map((b) => <BenchmarkRow key={b.symbol} row={b} />)}
          </tbody>
        </table>
      </div>
      <div className="mt-2 space-y-0.5 text-9 text-slate-500">
        <p>
          Agents: NAV (premium income and marks). Funds: distributions reinvested; "price only" leaves them
          out.
          {fundYtd.length > 0 && ` Fund YTD is measured from the close of ${byDate(fundYtd)}.`}
        </p>
        {incomeShown && (
          <p data-testid="scoreboard-method-note">
            JEPI, QYLD and YMAX: total return from the vendor&apos;s dividend-adjusted closes, as for SPY and QQQ
            and on the chart (every distribution reinvested); &quot;price only&quot; leaves the distributions out.
          </p>
        )}
        {partialYtd.length > 0 && (
          <p data-testid="scoreboard-partial-ytd">
            † Agent YTD runs from the agent&apos;s first NAV of the year ({byDate(partialYtd)}), a shorter
            window than the funds&apos; YTD: compare Since inception.
          </p>
        )}
        {hasDash && <p>— : cannot be computed from the stored series yet.</p>}
        {hasProvisional && (
          <p>
            * The as-of close is still provisional and may be revised by the vendor.
          </p>
        )}
        {/* The agents and the funds are marked at different times of day. */}
        <p data-testid="scoreboard-marking-note">
          Agent NAV is marked at the last exits run of each day (1:50 PM ET) and credits no dividends on
          shares held; fund values are 4:00 PM ET closes.
        </p>
        <p>Paper trading: simulated positions, no real money.</p>
        {data.generated_at && <p>Generated {data.generated_at}</p>}
      </div>
    </>
  );
};

const Scoreboard: React.FC<{ agentId?: string }> = ({ agentId }) => {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    apiService
      .getScoreboard()
      .then((data) => setState({ kind: 'ready', data }))
      .catch((err: unknown) =>
        setState({ kind: 'error', message: err instanceof Error ? err.message : 'Scoreboard unavailable' }));
  }, []);

  // Loaded on first open, never while collapsed; kept when it is closed and opened again
  useEffect(() => {
    if (open && state.kind === 'idle') load();
  }, [open, state.kind, load]);

  return (
    <section className="px-4 py-2 bg-slate-800/40 rounded-xl border border-slate-700/20" aria-label="Scoreboard">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="w-full min-h-[36px] flex items-center justify-between gap-2 text-xs font-medium text-slate-300
                   hover:text-white transition-colors"
      >
        Scoreboard details
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div id={bodyId} className="pt-1 pb-2">
          <h3 className="text-xs font-bold text-white mb-1">
            {agentId ? 'Scoreboard vs market' : 'Scoreboard: every agent vs market'}
          </h3>
          {(state.kind === 'idle' || state.kind === 'loading') && (
            <p className="text-xs text-slate-500">Loading scoreboard…</p>
          )}
          {state.kind === 'error' && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-xs" role="alert">
              {state.message}
              <button onClick={load} className="ml-3 underline">Retry</button>
            </div>
          )}
          {state.kind === 'ready' && <ScoreboardTable data={state.data} agentId={agentId} onRetry={load} />}
        </div>
      )}
    </section>
  );
};

export default Scoreboard;
